import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaWhatsapp, FaTelegramPlane } from 'react-icons/fa';
import { Check, X, Info, ArrowLeft, Calendar } from 'lucide-react';
import api from '@/lib/api';
import BookingForm from '@/components/BookingForm';
import NotFoundPage from '@/pages/NotFoundPage';
import { IDS } from '@/constants/testIds';

function TourSEO({ tour, slug }) {
  const canonical = `https://ali-india.ru/tour/${slug}`;
  const title = `${tour.title} — ${tour.duration_days} ${tour.duration_days === 1 ? 'день' : (tour.duration_days < 5 ? 'дня' : 'дней')} с гидом Али | Ali India Tours`;
  const rawSummary = (tour.summary || tour.subtitle || '').replace(/\s+/g, ' ').trim();
  const description = (rawSummary.slice(0, 300) || `${tour.title}. Групповой тур в Индию с русскоговорящим гидом. Продолжительность ${tour.duration_days} дней. Стоимость от $${tour.price_usd} на человека.`);
  const image = tour.hero_image || (tour.gallery && tour.gallery[0]) || 'https://ali-india.ru/og-cover.jpg';
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    name: tour.title,
    description,
    image,
    url: canonical,
    touristType: 'Culture',
    itinerary: (tour.program || []).map((p) => ({
      '@type': 'ItemList',
      name: `День ${p.day}: ${p.title}`,
      description: p.description,
    })),
    offers: {
      '@type': 'Offer',
      price: tour.price_usd,
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: canonical,
    },
    provider: {
      '@type': 'TravelAgency',
      name: 'Ali India Tours',
      url: 'https://ali-india.ru',
    },
  };
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}

export default function TourPage() {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [settings, setSettings] = useState({});
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 240]);
  const heroScale = useTransform(scrollY, [0, 800], [1, 1.15]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    api.get(`/tours/${slug}`).then((r) => setTour(r.data)).catch(() => setTour(false));
    api.get('/settings/public').then((r) => setSettings(r.data)).catch(() => {});
  }, [slug]);

  if (tour === null) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="index, follow" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center text-foreground/50">Загружаем…</div>
      </>
    );
  }
  if (tour === false) {
    return <NotFoundPage />;
  }

  const isFestival = tour.category === 'festival';
  const price = isFestival ? Math.round(tour.price_usd * 0.95) : tour.price_usd;
  const phone = (settings.whatsapp_phone || '+918958956564').replace(/[^\d]/g, '');
  const waLink = `https://wa.me/${phone}?text=Здравствуйте! Интересует тур «${tour.title}»`;
  const tgLink = `https://t.me/${(settings.telegram_username || '+918958956564').replace(/^\+/, '')}`;

  return (
    <div className="pt-0" data-testid="tour-page">
      <TourSEO tour={tour} slug={slug} />
      {/* Hero */}
      <section className="relative min-h-[85svh] w-full overflow-hidden">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0 -z-10">
          <img src={tour.hero_image} alt={tour.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-espresso/50 via-espresso/40 to-espresso" />
        </motion.div>

        <div className="container-x relative z-10 min-h-[85svh] flex flex-col justify-end pt-32 pb-16">
          <Link to="/" className="inline-flex items-center gap-2 text-foreground/70 hover:text-saffron text-xs uppercase tracking-widest2 mb-10 group">
            <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />
            Все туры
          </Link>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="overline mb-4">
            {tour.duration_days} дн. / {tour.duration_nights} ноч. · {tour.cities.join(' · ')}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 1, ease: [0.19, 1, 0.22, 1] }}
            className="font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-[7rem] leading-[0.9] text-foreground max-w-5xl"
          >
            {tour.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="mt-6 font-serif-display italic text-2xl md:text-3xl text-saffron"
          >
            {tour.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-6"
          >
            <div>
              {isFestival && (
                <div className="flex items-baseline gap-3 mb-1">
                  <span className="text-foreground/50 line-through">${tour.price_usd}</span>
                  <span className="text-xs uppercase tracking-widest2 font-bold bg-saffron text-foreground px-2 py-0.5">−5%</span>
                </div>
              )}
              <div className="flex items-baseline gap-3">
                <span className="font-serif-display text-6xl md:text-7xl text-foreground leading-none">${price}</span>
                <span className="text-foreground/60 text-sm uppercase tracking-widest2">/ с чел.</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                data-testid={IDS.tourWhatsappBtn}
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 justify-center px-6 py-4 bg-[#25D366] text-white text-xs uppercase tracking-widest2 font-bold hover:opacity-90 transition-opacity"
              >
                <FaWhatsapp size={18} /> WhatsApp
              </a>
              <a
                data-testid={IDS.tourTelegramBtn}
                href={tgLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 justify-center px-6 py-4 bg-[#26A5E4] text-white text-xs uppercase tracking-widest2 font-bold hover:opacity-90 transition-opacity"
              >
                <FaTelegramPlane size={18} /> Telegram
              </a>
              <a
                data-testid={IDS.tourBookBtn}
                href="#book"
                className="inline-flex items-center gap-3 justify-center px-6 py-4 border border-saffron text-saffron text-xs uppercase tracking-widest2 font-bold hover:bg-saffron hover:text-foreground transition-colors"
              >
                Форма заявки
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Summary + dates */}
      <section className="py-20 md:py-32 border-t border-border/60">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <p className="overline mb-6">О туре</p>
            <p className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-tight text-foreground/95 mb-10">
              {tour.summary}
            </p>
            {tour.highlights?.length > 0 && (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
                {tour.highlights.map((h) => (
                  <li key={h} className="flex gap-3 items-start text-foreground/80">
                    <span className="w-6 h-px bg-saffron mt-3 flex-shrink-0" />
                    <span className="font-light">{h}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <aside className="lg:col-span-4 space-y-8 lg:sticky lg:top-32 self-start">
            <div className="border border-border p-6">
              <p className="overline mb-4 flex items-center gap-2"><Calendar size={12} /> Ближайшие даты</p>
              <ul className="space-y-2">
                {(tour.next_dates || []).map((d) => (
                  <li key={d} className="font-serif-display text-lg text-foreground/90">{d}</li>
                ))}
              </ul>
            </div>
            <div className="border border-border p-6">
              <p className="overline mb-4">Основное</p>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between"><dt className="text-foreground/50">Продолжительность</dt><dd>{tour.duration_days} / {tour.duration_nights}</dd></div>
                <div className="flex justify-between"><dt className="text-foreground/50">Города</dt><dd className="text-right">{tour.cities.join(', ')}</dd></div>
                <div className="flex justify-between"><dt className="text-foreground/50">Тип</dt><dd>{tour.category === 'festival' ? 'Праздничный' : tour.category === 'girls' ? '8 марта' : 'Еженедельный'}</dd></div>
              </dl>
            </div>
          </aside>
        </div>
      </section>

      {/* Itinerary */}
      <section data-testid={IDS.tourItinerary} className="py-20 md:py-32 border-t border-border/60 bg-espresso">
        <div className="container-x">
          <div className="mb-12 md:mb-20 max-w-3xl">
            <p className="overline mb-4">День за днём</p>
            <h2 className="font-serif-display text-5xl md:text-6xl leading-none">Программа тура</h2>
          </div>
          <div className="space-y-0">
            {tour.program.map((day, i) => (
              <motion.div
                key={day.day}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: i * 0.04 }}
                className="grid grid-cols-12 gap-4 md:gap-8 py-8 md:py-10 border-t border-border/60"
              >
                <div className="col-span-2 md:col-span-1 font-serif-display text-3xl md:text-5xl italic text-saffron">
                  {String(day.day).padStart(2, '0')}
                </div>
                <div className="col-span-10 md:col-span-3">
                  <h3 className="font-serif-display text-xl md:text-3xl leading-tight">{day.title}</h3>
                </div>
                <div className="col-span-12 md:col-span-8">
                  <p className="text-foreground/75 text-base leading-relaxed font-light">{day.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Included / Not included / Important */}
      <section className="py-20 md:py-32 border-t border-border/60">
        <div className="container-x grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          <div>
            <p className="overline mb-6 text-saffron flex items-center gap-2"><Check size={12} /> Включено</p>
            <ul className="space-y-3">
              {(tour.included || []).map((x) => (
                <li key={x} className="text-foreground/85 text-sm md:text-base leading-relaxed flex gap-3">
                  <Check size={16} className="text-saffron mt-1 flex-shrink-0" />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="overline mb-6 text-crimson flex items-center gap-2"><X size={12} /> Не включено</p>
            <ul className="space-y-3">
              {(tour.not_included || []).map((x) => (
                <li key={x} className="text-foreground/70 text-sm md:text-base leading-relaxed flex gap-3">
                  <X size={16} className="text-crimson mt-1 flex-shrink-0" />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="overline mb-6 flex items-center gap-2"><Info size={12} /> Важно знать</p>
            <ul className="space-y-3">
              {(tour.important_info || []).map((x) => (
                <li key={x} className="text-foreground/85 text-sm md:text-base leading-relaxed flex gap-3">
                  <Info size={16} className="text-foreground/60 mt-1 flex-shrink-0" />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Booking form */}
      <section id="book" className="py-20 md:py-32 border-t border-border/60 bg-espresso">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline mb-4">Заявка</p>
            <h2 className="font-serif-display text-5xl md:text-6xl leading-[0.9] mb-6">Забронировать этот тур</h2>
            <p className="text-foreground/70 font-light leading-relaxed">
              Оставьте заявку — и Али напишет вам напрямую в WhatsApp или Telegram, чтобы уточнить детали и зафиксировать даты.
            </p>
            <div className="mt-8 border-l-2 border-saffron pl-6">
              <p className="font-serif-display italic text-xl text-foreground/90">
                «Обычно я отвечаю в течение часа — где бы я ни был.»
              </p>
              <p className="text-xs uppercase tracking-widest2 text-foreground/50 mt-3">— Али</p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <BookingForm tourSlug={tour.slug} tourTitle={tour.title} />
          </div>
        </div>
      </section>
    </div>
  );
}
